import type { ReportMetadata } from '../types';
import { getAusfuehrungsVertreter } from './projectRepresentatives';
import { getProjectRelation, type ProjectAccessRow, type ProjectAccessUser } from './projectAccess';
import { getEffectiveDueDate, heuteBerlin, parseDateLocal } from './dateService';
import { istSifaProjekt } from './projectType';

/**
 * Logik der Tourenplanung (GlobalMapDashboard): Begehungstage, Faelligkeit,
 * Reihenfolge der Stopps, Navigationslinks, Geocoding-Cache, Marker-Buendel.
 * Ohne React und ohne Leaflet, damit sie testbar bleibt.
 */

export interface GeoPunkt {
    lat: number;
    lng: number;
}

/** Luftlinie in km (Haversine). */
export const distanzKm = (a: GeoPunkt, b: GeoPunkt): number => {
    const R = 6371;
    const rad = (deg: number) => deg * (Math.PI / 180);
    const dLat = rad(b.lat - a.lat);
    const dLng = rad(b.lng - a.lng);
    const h = Math.sin(dLat / 2) ** 2
        + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

/** Ein Projekt, soweit die Tour es braucht (Zeile aus der Projektliste). */
export interface TourProjekt {
    status?: string | null;
    next_scheduled_date?: string | null;
    last_inspection_date?: string | null;
    updated_at?: string | null;
    data?: { metadata?: Partial<ReportMetadata> | null } | null;
}

/**
 * An welchen Wochentagen (0 = So … 6 = Sa) liegt das Projekt auf der Tour?
 *
 * EINE Regel fuer Liste und Karte: die festen Begehungstage des
 * Verantwortlichen und seiner Vertreter. Erst wenn keiner einen Tag traegt,
 * zaehlt der Wochentag des naechsten Termins. SiFa ohne Turnus hat keinen
 * Begehungstag — der rohe DB-Termin stammt aus dem Altbestand (D1),
 * getEffectiveDueDate kennt die Ausnahme. Leer = „nicht zugewiesen".
 *
 * Vertreter heisst hier Ausfuehrungs-Vertreter: Die Verantwortliche
 * Planungsphase macht keine Begehungen. Traegt ihr Eintrag noch Wochentage
 * (etwa aus der Zeit, als sie als normaler Stellvertreter galt), setzten die
 * das Projekt sonst auf einen Tourtag, an dem niemand hinfaehrt.
 */
export const begehungstage = (p: TourProjekt): number[] => {
    const meta = (p.data?.metadata || {}) as ReportMetadata;
    const interval = meta.intervalConfig;

    // Nur gueltige Wochentage zaehlen — ein Altwert wie [7] darf den
    // Rueckfall auf den Termin nicht verhindern.
    const gueltig = (t: number) => Number.isInteger(t) && t >= 0 && t <= 6;
    const tage = new Set<number>((interval?.preferredWeekdays || []).filter(gueltig));
    getAusfuehrungsVertreter(meta).slice(1).forEach(r => (r.weekdays || []).filter(gueltig).forEach(t => tage.add(t)));
    if (tage.size > 0) return Array.from(tage).sort((a, b) => a - b);

    const naechster = istSifaProjekt(meta)
        ? getEffectiveDueDate(p)
        : (p.next_scheduled_date || interval?.nextScheduledDate);
    const datum = parseDateLocal(naechster);
    return datum ? [datum.getDay()] : [];
};

/**
 * Gehoert das Projekt auf MEINE Begehungskarte („Nur meine")? Zustaendig fuer
 * die Ausfuehrung oder aktive Urlaubsvertretung.
 *
 * Bewusst nicht `isResponsible`: Die Karte plant Touren nach Faelligkeit,
 * und wer nur Verantwortliche Planungsphase ist, faehrt nicht zur Begehung.
 * Zugriff und Projektliste behaelt sie trotzdem (`getProjectRelation`).
 */
export const istMeineBegehung = (
    p: ProjectAccessRow | null | undefined,
    user: ProjectAccessUser | null | undefined,
): boolean => {
    const rel = getProjectRelation(p, user);
    return rel.isResponsibleAusfuehrung || rel.isSubstitute;
};

export interface Faelligkeit {
    /** Termin als `YYYY-MM-DD`. */
    datum: string;
    /** Kalendertage bis zum Termin; negativ = ueberfaellig. */
    tage: number;
}

/**
 * Naechster Begehungstermin gegen den heutigen Berliner Tag. `null` fuer
 * Ruht/Beendet/Archiv, SiFa und Projekte ohne Termin (getEffectiveDueDate).
 */
export const faelligkeit = (p: TourProjekt, heute: string = heuteBerlin()): Faelligkeit | null => {
    const datum = getEffectiveDueDate(p);
    const termin = parseDateLocal(datum);
    const tag = parseDateLocal(heute);
    if (!datum || !termin || !tag) return null;
    return { datum: datum.slice(0, 10), tage: Math.round((termin.getTime() - tag.getTime()) / 86_400_000) };
};

const wegLaenge = (punkte: GeoPunkt[]): number =>
    punkte.reduce((summe, p, i) => (i === 0 ? 0 : summe + distanzKm(punkte[i - 1], p)), 0);

/** Naechster-Nachbar ab `anfang`. */
const naechsterNachbar = <T extends GeoPunkt>(anfang: GeoPunkt, rest: T[]): T[] => {
    const offen = [...rest];
    const weg: T[] = [];
    let hier = anfang;
    while (offen.length > 0) {
        let best = 0;
        for (let i = 1; i < offen.length; i++) {
            if (distanzKm(hier, offen[i]) < distanzKm(hier, offen[best])) best = i;
        }
        hier = offen.splice(best, 1)[0];
        weg.push(hier as T);
    }
    return weg;
};

/** 2-opt fuer einen OFFENEN Weg; `fest` = so viele Punkte vorn bleiben stehen (Startpunkt). */
const zweiOpt = (weg: GeoPunkt[], fest: number): GeoPunkt[] => {
    const p = [...weg];
    const d = (a: GeoPunkt | undefined, b: GeoPunkt | undefined) => (a && b ? distanzKm(a, b) : 0);
    let besser = true;
    for (let runde = 0; besser && runde < 50; runde++) {
        besser = false;
        for (let i = fest; i < p.length - 1; i++) {
            for (let k = i + 1; k < p.length; k++) {
                const vor = d(p[i - 1], p[i]) + d(p[k], p[k + 1]);
                const nach = d(p[i - 1], p[k]) + d(p[i], p[k + 1]);
                if (nach < vor - 1e-9) {
                    p.splice(i, k - i + 1, ...p.slice(i, k + 1).reverse());
                    besser = true;
                }
            }
        }
    }
    return p;
};

export interface Tour<T> {
    /** Stopps in Fahrreihenfolge (ohne Startpunkt). */
    stopps: T[];
    /** Luftlinie vom Start (falls gegeben) ueber alle Stopps, in km. */
    km: number;
}

/**
 * Kurze Reihenfolge der Stopps (Luftlinie, offener Weg, kein Rueckweg):
 * Naechster-Nachbar, danach 2-opt. Ohne Start wird jeder Stopp als Anfang
 * probiert und der kuerzeste Weg genommen. Fuer eine Tagestour (einige
 * Dutzend Stopps) schnell genug; keine Strassen, keine Fahrzeiten.
 */
export const ordneTour = <T extends GeoPunkt>(start: GeoPunkt | null, stopps: T[]): Tour<T> => {
    if (stopps.length === 0) return { stopps: [], km: 0 };

    let weg: GeoPunkt[];
    if (start) {
        weg = zweiOpt([start, ...naechsterNachbar(start, stopps)], 1);
    } else {
        // Hoechstens 25 Anfaenge, gleichmaessig verteilt: die Admin-Ansicht
        // kann an einem Tag Hunderte Stopps tragen, alle zu probieren waere kubisch.
        const schritt = Math.max(1, Math.ceil(stopps.length / 25));
        let bester: GeoPunkt[] = [];
        let besteKm = Infinity;
        for (let i = 0; i < stopps.length; i += schritt) {
            const kandidat = [stopps[i], ...naechsterNachbar(stopps[i], stopps.filter((_, j) => j !== i))];
            const km = wegLaenge(kandidat);
            if (km < besteKm) { besteKm = km; bester = kandidat; }
        }
        weg = zweiOpt(bester, 0);
    }
    return {
        stopps: (start ? weg.slice(1) : weg) as T[],
        km: wegLaenge(weg),
    };
};

/** Google Maps nimmt hoechstens 9 Zwischenziele; mehr Punkte = mehrere Links. */
export const MAX_ZWISCHENZIELE = 9;

const koordinate = (p: GeoPunkt) => `${p.lat.toFixed(6)},${p.lng.toFixed(6)}`;

/**
 * Navigationslinks (Google Maps, Auto) fuer die Punkte in Reihenfolge — der
 * erste ist der Start. Mehr als 11 Punkte werden in Teilstrecken geteilt; das
 * Ziel eines Teils ist der Start des naechsten. Ein einzelner Punkt wird Ziel
 * ab dem aktuellen Standort.
 */
export const googleMapsLinks = (punkte: GeoPunkt[]): string[] => {
    const basis = 'https://www.google.com/maps/dir/?api=1&travelmode=driving';
    if (punkte.length === 0) return [];
    if (punkte.length === 1) return [`${basis}&destination=${koordinate(punkte[0])}`];

    const proLink = MAX_ZWISCHENZIELE + 2;
    const links: string[] = [];
    for (let i = 0; i < punkte.length - 1; i += proLink - 1) {
        const teil = punkte.slice(i, i + proLink);
        const zwischen = teil.slice(1, -1).map(koordinate).join('|');
        links.push(
            `${basis}&origin=${koordinate(teil[0])}&destination=${koordinate(teil[teil.length - 1])}`
            + (zwischen ? `&waypoints=${encodeURIComponent(zwischen)}` : ''),
        );
    }
    return links;
};

// ─── Geocoding mit Cache ────────────────────────────────────────────────────

// Versionsteil im Schluessel: aendert sich das Format, wird der alte Bestand
// einfach nicht mehr gelesen. Nach 90 Tagen wird neu gefragt, damit ein
// einmal falscher Treffer nicht fuer immer liegen bleibt.
const CACHE_PREFIX = 'map.geocode.v1.';
export const GEOCODE_CACHE_TAGE = 90;

const cacheLesen = (adresse: string, jetzt: number): (GeoPunkt & { address: string }) | null => {
    try {
        const roh = window.localStorage.getItem(CACHE_PREFIX + adresse);
        const wert = roh ? JSON.parse(roh) : null;
        if (!wert || typeof wert.lat !== 'number' || typeof wert.lng !== 'number' || typeof wert.ts !== 'number') return null;
        if (jetzt - wert.ts > GEOCODE_CACHE_TAGE * 86_400_000) return null;
        return { lat: wert.lat, lng: wert.lng, address: String(wert.address || '') };
    } catch {
        return null;
    }
};

const cacheSchreiben = (adresse: string, wert: GeoPunkt & { address: string }, jetzt: number) => {
    try {
        window.localStorage.setItem(CACHE_PREFIX + adresse, JSON.stringify({ ...wert, ts: jetzt }));
    } catch {
        // Speicher voll oder gesperrt: dann eben ohne Cache.
    }
};

/**
 * Feste Adresse (Firma, Zuhause) verorten. Das Ergebnis bleibt im Browser
 * liegen (90 Tage): Nominatim erlaubt eine Anfrage pro Sekunde, und die
 * Karte fragte bei jedem Oeffnen neu. Fehlschlaege werden nicht gemerkt.
 */
export const geocodeMitCache = async (adresse: string): Promise<(GeoPunkt & { address: string }) | null> => {
    const schluessel = adresse.trim().toLowerCase();
    if (!schluessel) return null;
    const jetzt = Date.now();
    const gemerkt = cacheLesen(schluessel, jetzt);
    if (gemerkt) return gemerkt;

    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(adresse)}&limit=1`;
    const res = await fetch(url);
    const data = await res.json();
    if (!Array.isArray(data) || data.length === 0) return null;
    const treffer = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon), address: String(data[0].display_name || adresse) };
    if (!Number.isFinite(treffer.lat) || !Number.isFinite(treffer.lng)) return null;
    cacheSchreiben(schluessel, treffer, jetzt);
    return treffer;
};

// ─── Marker-Buendel ─────────────────────────────────────────────────────────

/** Ein Punkt in Bildschirm-Pixeln (Leaflet `map.project` bei aktuellem Zoom). */
export interface PixelPunkt<T> {
    x: number;
    y: number;
    wert: T;
}

/**
 * Punkte in ein Raster von `zelle` Pixeln einsortieren. Jede Gruppe mit mehr
 * als einem Punkt wird ein Buendel. Reihenfolge der Gruppen = erste Fundstelle.
 */
export const buendeln = <T>(punkte: PixelPunkt<T>[], zelle: number): T[][] => {
    const gruppen = new Map<string, T[]>();
    punkte.forEach(p => {
        const schluessel = `${Math.floor(p.x / zelle)}:${Math.floor(p.y / zelle)}`;
        const gruppe = gruppen.get(schluessel);
        if (gruppe) gruppe.push(p.wert);
        else gruppen.set(schluessel, [p.wert]);
    });
    return Array.from(gruppen.values());
};
